import { useState } from "react";
import { Box, Typography, Divider, Stack, Button, Collapse, IconButton } from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import WorkHistoryIcon from "@mui/icons-material/WorkHistory";
import EventNoteIcon from "@mui/icons-material/EventNote";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { colors } from "../../../ui/colors";
import { useMediaQuery } from "@uidotdev/usehooks";

interface InfoItemProps {
  icon: React.ComponentType<{ sx?: any }>;
  title: string;
  description: string;
}

export const JobsDetailsInfo = () => {
  const isXs = useMediaQuery("(max-width:600px)");

  const [open, setOpen] = useState(false);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        p: 2,
        my: 1,
        mx: 2,
        borderRadius: "6px",
        background: "var(--fx-surface-sunken, #F8FAFC)",
      }}
    >
      <Box
        onClick={() => setOpen(!open)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <InfoIcon sx={{ color: "#bbdefb" }} />
          <Typography fontWeight={500}>How scheduling works</Typography>
        </Box>

        <IconButton
          size="small"
          sx={{
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "0.2s ease",
          }}
        >
          <ExpandMoreIcon />
        </IconButton>
      </Box>

      <Collapse in={open}>
        <Divider
          sx={{
            my: 2,
            opacity: 0.4,
          }}
        />

        {/* Content */}
        <Stack direction={isXs ? "column" : "row"} spacing={3} alignItems="stretch">
          <InfoItem
            icon={WorkHistoryIcon}
            title="Job (container)"
            description="Holds the service, location, and pricing. It acts as the parent entity."
          />

          <InfoItem
            icon={EventNoteIcon}
            title="Job Events (visits)"
            description="Each event represents a scheduled visit with its own date, time, workforce, and status."
          />
        </Stack>
      </Collapse>
    </Box>
  );
};

const InfoItem = ({ icon: Icon, title, description }: InfoItemProps) => (
  <Box sx={SHARED_STYLES.infoItem}>
    <Icon sx={SHARED_STYLES.icon} />
    <Stack spacing={0.5}>
      <Typography sx={SHARED_STYLES.title}>{title}</Typography>
      <Typography sx={SHARED_STYLES.body}>{description}</Typography>
    </Stack>
  </Box>
);

const SHARED_STYLES = {
  title: {
    color: colors.gray.base,
    fontSize: "0.95rem",
    fontWeight: 500,
  },
  body: {
    color: colors.gray[400],
    fontSize: "0.85rem",
    lineHeight: 1.4,
  },
  icon: {
    color: "#bbdefb",
    fontSize: "2.2rem",
  },
  infoItem: {
    display: "flex",
    gap: 2,
    flex: 1,
    minWidth: 0,
  },
} as const;
